import { SearchOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { type BaseSelectRef } from 'rc-select';
import { type MouseEvent as ReactMouseEvent, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { filterOption } from 'components/choosers/helpers';
import {
  CustomFilterContainer,
  CustomFilterPopover,
  FilterActions,
} from 'components/data/helpers/filters.style';
import { DataTableContext } from 'components/DataTable';
import {
  FilterMultiSelectContainer,
  handleTableFilterActionMouseDown,
  TableFilterMultiSelect,
} from 'components/DataTable/customFilters/TableFilterMultiSelect';
import { AutoFocus } from 'horizon/components/AutoFocus';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import { useGetConfiguredAssetAttributeValuesQuery } from 'types/atlas-graphql';

/**
 * Table column filter component for filtering asset attribute values (applicable only on asset tables)
 *
 * This component takes an asset attribute field name, and optionally an asset type
 * name as props, and provides a list of existing values for that attribute
 * (and type, if appropriate) in the current organization.
 * If site and/or turbine filters are applied in the table, the values are limited to those at
 * the site(s) or on the selected turbine(s).
 *
 * Additionally, there is an optional `independentFieldFilterKey` prop, which, when provided, will result in
 * only options that are valid combinations with any filtered values under that key or are legacy non-saav values
 * (i.e. if filtering turbines and "Vestas" is selected as a make filter, only valid Vestas models and non-saav
 * models will be available for selection here).
 */
type AssetAttributeSelectFilterProps = CustomFilterDropdownProps & {
  fieldName: string;
  assetTypeName?: string;
  independentFieldFilterKey?: string;
};

export const AssetAttributeSelectFilter: React.FunctionComponent<
  AssetAttributeSelectFilterProps
> = ({
  fieldName,
  assetTypeName,
  independentFieldFilterKey,
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<BaseSelectRef>(null);
  // Keep search text across selections so users can bulk-select from a filtered list.
  const [searchValue, setSearchValue] = useState('');

  const { filteredInfo } = useContext(DataTableContext);
  const {
    selectedSiteIds,
    selectedTurbineIds,
    selectedInependentFieldValues,
  }: {
    selectedSiteIds: string[];
    selectedTurbineIds: string[];
    selectedInependentFieldValues: string[];
  } = useMemo(
    () => ({
      selectedSiteIds: filteredInfo?.site ?? [],
      selectedTurbineIds: filteredInfo?.asset_turbine ?? [],
      selectedInependentFieldValues: !!independentFieldFilterKey
        ? filteredInfo?.[independentFieldFilterKey] ?? []
        : [],
    }),
    [JSON.stringify(filteredInfo), independentFieldFilterKey]
  );

  const { data, loading } = useGetConfiguredAssetAttributeValuesQuery({
    variables: {
      input: {
        fieldName,
        ...(selectedSiteIds?.length ? { siteIds: selectedSiteIds } : {}),
        ...(selectedTurbineIds?.length ? { ancestorAssetIds: selectedTurbineIds } : {}),
        ...(assetTypeName ? { assetTypeNames: [assetTypeName] } : {}),
      },
      fieldNameForDependentCombinationOptions: fieldName,
    },
  });
  const options = useMemo(() => {
    const _options = [...(data?.configuredAssetFieldValues?.existingValues ?? [])].sort();

    /**
     * If the filter is for a dependent field (e.g. model), and its corresponding independent field (e.g. make)
     * has been filtered, filter the options down to only those that either
     *   a) are a valid combination for one of the selected independent values, or
     *   b) are not a standardized value for combination (i.e. we still want to be able to filter on legacy model
     * values that are not SAAV)
     *
     * If the independent field has not been filtered, all existing options will be available in the dependent field's column
     */
    if (
      data?.assetFieldDefinition.dependentCombinationOptions?.length &&
      selectedInependentFieldValues.length
    ) {
      const { dependentOptionsForSelectedIndependentValues, nonStandardOptions } =
        data.assetFieldDefinition.dependentCombinationOptions.reduce(
          (acc, value) => {
            if (value.independentFieldValue) {
              if (selectedInependentFieldValues.includes(value.independentFieldValue)) {
                const dependentFieldOptions = value.dependentFieldOptions ?? [];
                return {
                  dependentOptionsForSelectedIndependentValues: [
                    ...acc.dependentOptionsForSelectedIndependentValues,
                    ...dependentFieldOptions,
                  ],
                  nonStandardOptions: acc.nonStandardOptions.filter(
                    value => !dependentFieldOptions.includes(value)
                  ),
                };
              } else {
                const dependentFieldOptions = value.dependentFieldOptions ?? [];
                return {
                  dependentOptionsForSelectedIndependentValues:
                    acc.dependentOptionsForSelectedIndependentValues,
                  nonStandardOptions: acc.nonStandardOptions.filter(
                    value => !dependentFieldOptions.includes(value)
                  ),
                };
              }
            }

            return acc;
          },
          {
            dependentOptionsForSelectedIndependentValues: [],
            nonStandardOptions: _options,
          }
        );

      // return all existing values that are valid combinations with filtered independent values or are non-saav
      return _options.filter(value =>
        [...dependentOptionsForSelectedIndependentValues, ...nonStandardOptions].includes(value)
      );
    }

    return _options;
  }, [data?.configuredAssetFieldValues?.existingValues, selectedInependentFieldValues]);

  const handleConfirm = () => {
    setSearchValue('');
    confirm({ closeDropdown: true });
  };

  // clearFilters only resets the draft; confirm applies it.
  const handleReset = () => {
    setSearchValue('');
    clearFilters && clearFilters();
    confirm({ closeDropdown: true });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
  };

  return (
    <AutoFocus elementToFocus={inputRef}>
      <CustomFilterPopover className="ant-table-filter-dropdown">
        <CustomFilterContainer>
          <FilterMultiSelectContainer>
            <TableFilterMultiSelect
              id="asset-attribute-filter-dropdown"
              ref={inputRef}
              placeholder={t('common.selectValuePlaceholder', 'Select value...')}
              loading={loading}
              allowClear
              showSearch
              filterOption={filterOption}
              value={(selectedKeys as string[]) ?? []}
              onChange={setSelectedKeys}
              searchValue={searchValue}
              onSearch={setSearchValue}
              onOpenChange={handleOpenChange}
              popupMatchSelectWidth={false}
              suffixIcon={<SearchOutlined aria-hidden />}
              options={options.map(value => ({
                value,
                label: value,
                className: 'asset-attribute-filter-option',
              }))}
            />
          </FilterMultiSelectContainer>
        </CustomFilterContainer>
        <FilterActions>
          <Button
            type="link"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleReset)
            }
          >
            {t('table.reset', 'Reset')}
          </Button>
          <Button
            type="primary"
            size="small"
            onMouseDown={(event: ReactMouseEvent) =>
              handleTableFilterActionMouseDown(event, handleConfirm)
            }
          >
            {t('table.ok', 'OK')}
          </Button>
        </FilterActions>
      </CustomFilterPopover>
    </AutoFocus>
  );
};
